<template>
  <ACard id="settlement-info" class="stm-info">
    <template #title>
      <h5 class="stm-info__h">정산 정보</h5>
    </template>

    <div v-if="loading" class="stm-info__loading">
      <div class="p-spinner p-spinner--lg"></div>
      <p>로딩 중...</p>
    </div>

    <template v-else>
      <h6 class="stm-info__section-h">
        <i class="material-icons-round">analytics</i>
        수익 요약
      </h6>

      <div v-if="loadFailed" class="stm-info__load-error">
        <i class="material-icons-round">error_outline</i>
        <span>{{ loadErrorMessage }} 아래 금액은 실제 값이 아닙니다.</span>
      </div>

      <div class="stm-info__stats">
        <div class="stm-info__stat">
          <div class="stm-info__stat-icon stm-info__stat-icon--info">
            <i class="material-icons-round">account_balance_wallet</i>
          </div>
          <div>
            <p class="stm-info__stat-label">총 수익</p>
            <p class="stm-info__stat-val">{{ formatCurrency(settlementData.totalRevenue) }}</p>
            <small class="stm-info__stat-note">지급 완료 누적</small>
          </div>
        </div>
        <div class="stm-info__stat">
          <div class="stm-info__stat-icon stm-info__stat-icon--ok">
            <i class="material-icons-round">payments</i>
          </div>
          <div>
            <!--
              지표가 "정산월 = 이번 달"에서 "이번 달에 지급 완료된 금액"으로 바뀌었습니다.
              "수익"으로 두면 발생 기준으로 읽혀, 미지급분이 빠진 값을 매출 누락으로 오해합니다.
            -->
            <p class="stm-info__stat-label">{{ currentMonth }} 지급액</p>
            <p class="stm-info__stat-val">{{ formatCurrency(settlementData.monthlyRevenue) }}</p>
            <small v-if="!loadFailed" class="stm-info__stat-note">지급 완료 기준 · 지급 이력 없으면 0</small>
          </div>
        </div>
        <div class="stm-info__stat">
          <div class="stm-info__stat-icon stm-info__stat-icon--warn">
            <i class="material-icons-round">schedule</i>
          </div>
          <div>
            <p class="stm-info__stat-label">정산 대기</p>
            <p class="stm-info__stat-val">{{ formatCurrency(settlementData.pendingSettlement) }}</p>
            <small class="stm-info__stat-note">계산완료 + 지급서발행 · 아직 미지급</small>
          </div>
        </div>
      </div>

      <div class="stm-info__divider"></div>

      <h6 class="stm-info__section-h">
        <i class="material-icons-round">history</i>
        최근 정산 내역
      </h6>
      <AEmpty
        v-if="recentSettlements.length === 0"
        title="정산 내역이 없습니다"
        size="sm"
      />
      <ATable
        v-else
        :columns="settlementCols"
        :rows="recentSettlements"
        row-key="idx"
      >
        <template #cell:settlementMonth="{ row }">
          <strong>{{ row.settlementMonth }}</strong>
        </template>
        <template #cell:textbookTitle="{ row }">{{ row.textbookTitle }}</template>
        <template #cell:amount="{ row }">
          <span class="stm-info__num">{{ formatCurrency(row.amount, row.currency) }}</span>
        </template>
        <template #cell:status="{ row }">
          <APill :kind="statusKind(row.status)">{{ getStatusLabel(row.status) }}</APill>
        </template>
      </ATable>

      <div class="stm-info__divider"></div>

      <h6 class="stm-info__section-h">
        <i class="material-icons-round">settings</i>
        정산 설정
      </h6>
      <div class="p-form-grid p-form-grid--3">
        <div class="p-field">
          <label class="p-field__label">선호 화폐</label>
          <!--
            선택지를 KRW·USD 로 좁히지 않습니다. 백엔드 권고(docs/정산_프론트엔드개발_가이드.md §6.3)의 제한 대상은
            결제로 받는 통화(가격표·쿠폰)인데, 여기는 에디터에게 인세를 지급할 때 환산할 통화라
            에디터가 사는 나라의 통화를 고를 수 있어야 합니다. 자동 등록으로 새 통화가 늘어나는
            것도 이 화면에는 도움이 됩니다.
          -->
          <select v-model="preferredCurrency" class="p-input" :disabled="currenciesLoading">
            <option v-if="currenciesLoading" value="">로딩 중...</option>
            <option v-for="currency in currenciesList" :key="currency.code" :value="currency.code">
              {{ currencyOptionLabel(currency) }}
            </option>
          </select>
          <p class="p-field__hint">인세 정산 시 적용될 화폐</p>
        </div>
      </div>

      <div class="stm-info__divider"></div>

      <h6 class="stm-info__section-h">
        <i class="material-icons-round">account_balance</i>
        계좌 정보
      </h6>
      <div class="p-form-grid p-form-grid--3">
        <div class="p-field">
          <label class="p-field__label">은행명</label>
          <select v-model="bankInfo.bankName" class="p-input">
            <option value="">선택</option>
            <option v-for="bank in bankOptions" :key="bank" :value="bank">
              {{ bankList.includes(bank) ? bank : `${bank} (지원하지 않음 · 다시 선택)` }}
            </option>
          </select>
        </div>
        <div class="p-field">
          <label class="p-field__label">계좌번호</label>
          <input v-model="bankInfo.accountNumber" class="p-input" placeholder="예: 123-456-789012" />
          <p class="p-field__hint">숫자와 하이픈(-)만 입력 가능</p>
        </div>
        <div class="p-field">
          <label class="p-field__label">예금주</label>
          <input v-model="bankInfo.accountHolder" class="p-input" placeholder="예금주명 입력" />
        </div>
      </div>

      <div class="stm-info__actions">
        <ABtn
          kind="yellow"
          icon="save"
          size="sm"
          :disabled="saving"
          :loading="saving"
          @click="saveSettlementSettings"
        >{{ saving ? '저장 중' : '정산 설정 저장' }}</ABtn>
      </div>
    </template>
  </ACard>
</template>

<script>
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import { formatMoney } from "@/utils/currency";

export default {
  name: "SettlementInfoCard",
  components: { ACard, ABtn, APill, ATable, AEmpty },
  props: {
    editorIdx: { type: [Number, String], required: true },
  },
  emits: ['save-bank-info'],
  data() {
    return {
      loading: false,
      saving: false,
      loadFailed: false,
      loadErrorMessage: "",
      settlementData: {
        totalRevenue: 0,
        monthlyRevenue: 0,
        pendingSettlement: 0,
        currency: "KRW",
      },
      recentSettlements: [],
      bankInfo: {
        bankName: "",
        accountNumber: "",
        accountHolder: "",
      },
      originalBankInfo: null,
      preferredCurrency: "KRW",
      originalPreferredCurrency: "KRW",
      currenciesList: [],
      currenciesLoading: false,
      // 서버가 이 21개만 허용합니다(정산_에디터_API.md). 목록에 없는 값을 보내면 400 이라
      // 임의로 늘리거나 줄이지 말고 문서와 같은 항목·순서를 유지하세요.
      bankList: [
        "KB국민은행", "신한은행", "우리은행", "하나은행", "SC제일은행", "한국씨티은행",
        "케이뱅크", "카카오뱅크", "토스뱅크", "NH농협은행", "수협은행", "대구은행",
        "부산은행", "광주은행", "전북은행", "경남은행", "제주은행", "IBK기업은행",
        "KDB산업은행", "새마을금고", "우체국",
      ],
      settlementCols: [
        { key: 'settlementMonth', label: '정산월',   width: 140 },
        { key: 'textbookTitle',   label: '교재' },
        { key: 'amount',          label: '정산금액', width: 130, align: 'right' },
        { key: 'status',          label: '상태',     width: 110, align: 'center' },
      ],
    };
  },
  computed: {
    currentMonth() {
      const now = new Date();
      return `${now.getFullYear()}년 ${now.getMonth() + 1}월`;
    },

    // 저장된 은행명이 지원 목록에서 빠진 경우(예: 구 목록의 "신협") select 는 빈칸으로 보이지만
    // 모델에는 그대로 남아 저장 시 그 값이 전송됩니다. 값이 보이게 남겨 두고 저장에서 막습니다.
    bankOptions() {
      const saved = this.bankInfo.bankName;
      return saved && !this.bankList.includes(saved) ? [...this.bankList, saved] : this.bankList;
    },
  },
  watch: {
    editorIdx: {
      handler(newVal) {
        if (newVal) {
          this.loadSettlementInfo();
          this.loadCurrencies();
        }
      },
      immediate: true,
    },
  },
  methods: {
    async loadSettlementInfo() {
      if (!this.editorIdx) return;
      this.loading = true;
      this.loadFailed = false;
      this.loadErrorMessage = "";
      try {
        const res = await this.$axios({
          method: "get",
          url: `/settlement/editor/${this.editorIdx}/summary`,
          loadingBar: false,
        });
        if (res.status === 200 && res.data && res.data.success) {
          const data = res.data.data;
          this.settlementData = {
            totalRevenue: parseFloat(data.totalRevenue) || 0,
            monthlyRevenue: parseFloat(data.monthlyRevenue) || 0,
            pendingSettlement: parseFloat(data.pendingSettlement) || 0,
            currency: data.currency || "KRW",
          };
          this.recentSettlements = (data.recentSettlements || []).map((item) => ({
            idx: item.idx,
            settlementMonth: `${item.settlementYear}년 ${item.settlementMonth}월`,
            textbookTitle: item.textbookTitle || '-',
            amount: parseFloat(item.royaltyAmount) || 0,
            currency: item.royaltyCurrency || 'KRW',
            status: item.status || 'pending',
          }));
          if (data.bankInfo) {
            this.bankInfo = {
              bankName: data.bankInfo.bankName || "",
              accountNumber: data.bankInfo.accountNumber || "",
              accountHolder: data.bankInfo.accountHolder || "",
            };
            this.originalBankInfo = { ...this.bankInfo };
          }
          const prefCurrency = data.bankInfo?.preferredCurrency || data.preferredCurrency || data.currency || "KRW";
          this.preferredCurrency = prefCurrency;
          this.originalPreferredCurrency = prefCurrency;
        }
      } catch (error) {
        console.error("정산 정보 로드 실패:", error);
        // 실패로 0 이 된 것을 "지급 이력이 없어 0"으로 읽으면 오독입니다 — 두 상태를 구분해 표시합니다.
        this.loadFailed = true;
        this.loadErrorMessage = error.response?.data?.detail
          || error.response?.data?.error?.message
          || "정산 정보를 불러오지 못했습니다.";
        this.settlementData = {
          totalRevenue: 0,
          monthlyRevenue: 0,
          pendingSettlement: 0,
          currency: "KRW",
        };
        this.recentSettlements = [];
      } finally {
        this.loading = false;
      }
    },

    // 통화 결정(인자 → 정산 통화 → KRW)만 여기서 하고, 표기는 공용 헬퍼에 맡깁니다.
    // 자릿수를 KRW 만 0 으로 두면 JPY·VND(소수 단위 없음)에 .00 이 붙습니다.
    formatCurrency(amount, currency = null) {
      return formatMoney(amount, currency || this.settlementData.currency || "KRW");
    },

    statusKind(status) {
      const map = {
        completed: 'ok', paid: 'ok',
        pending: 'warn', calculated: 'warn', processing: 'info', invoiced: 'info',
        cancelled: 'err',
      };
      return map[status] || 'default';
    },

    getStatusLabel(status) {
      // calculated 는 문서상 정식 상태인데 빠져 있어 원문 그대로 노출됐습니다(정산 대기 금액의 구성 요소).
      const labels = {
        completed: '완료', paid: '지급완료', pending: '대기', calculated: '계산완료',
        processing: '처리중', invoiced: '지급서발행', cancelled: '취소',
      };
      return labels[status] || status;
    },

    /**
     * "KRW (₩)" 처럼 코드 옆에 기호를 보여 줍니다.
     * 자동 등록된 통화는 기호를 모르면 icon 에 코드가 그대로 들어오고(docs/정산_프론트엔드개발_가이드.md §6.3),
     * name 도 비어 있을 수 있어 그대로 찍으면 "SEK (SEK)" 나 "SEK ()" 가 됩니다.
     */
    currencyOptionLabel(currency) {
      const symbol = currency.icon === currency.code ? "" : currency.icon || currency.name;
      return symbol ? `${currency.code} (${symbol})` : currency.code;
    },

    async loadCurrencies() {
      this.currenciesLoading = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: "/common/currencies",
          loadingBar: false,
        });
        if (res.status === 200 && res.data) {
          this.currenciesList = Array.isArray(res.data) ? res.data : res.data.currencies || [];
        }
      } catch (error) {
        console.error("화폐 목록 로드 실패:", error);
        this.currenciesList = [
          { code: "KRW", name: "원", icon: "₩" },
          { code: "USD", name: "달러", icon: "$" },
          { code: "EUR", name: "유로", icon: "€" },
          { code: "JPY", name: "엔", icon: "¥" },
        ];
      } finally {
        this.currenciesLoading = false;
      }
    },

    async saveSettlementSettings() {
      const bankInfoChanged = !this.originalBankInfo ||
          this.bankInfo.bankName !== this.originalBankInfo.bankName ||
          this.bankInfo.accountNumber !== this.originalBankInfo.accountNumber ||
          this.bankInfo.accountHolder !== this.originalBankInfo.accountHolder;
      const currencyChanged = this.preferredCurrency !== this.originalPreferredCurrency;

      if (!bankInfoChanged && !currencyChanged) {
        alert("변경된 내용이 없습니다.");
        return;
      }

      // 계좌 3개 필드는 서버에서 전부 Required 입니다(정산_에디터_API.md API 2 요청 필드 표).
      // 선호 화폐도 이 API 로만 저장되므로, 계좌가 비어 있으면 화폐만 바꿔 저장할 수 없습니다.
      // 예전에는 빈 문자열을 그대로 보내 "지원하지 않는 은행입니다" 400 으로만 튕겼습니다.
      const hasBankInfo = this.bankInfo.bankName || this.bankInfo.accountNumber || this.bankInfo.accountHolder;
      if (!hasBankInfo) {
        alert("계좌 정보(은행·계좌번호·예금주)를 함께 입력해야 저장할 수 있습니다.");
        return;
      }
      if (!this.bankInfo.bankName) { alert("은행을 선택해주세요."); return; }
      if (!this.bankList.includes(this.bankInfo.bankName)) {
        alert("지원하지 않는 은행입니다. 목록에서 다시 선택해주세요.");
        return;
      }
      if (!this.bankInfo.accountNumber) { alert("계좌번호를 입력해주세요."); return; }
      // 서버 검증과 같은 규칙(숫자·하이픈)입니다. 여기서 걸러야 저장 버튼이 400 으로 헛돌지 않습니다.
      if (!/^[0-9-]+$/.test(this.bankInfo.accountNumber)) {
        alert("계좌번호는 숫자와 하이픈(-)만 입력 가능합니다.");
        return;
      }
      if (!this.bankInfo.accountHolder) { alert("예금주를 입력해주세요."); return; }

      this.saving = true;
      try {
        const res = await this.$axios({
          method: "put",
          url: `/settlement/editor/${this.editorIdx}/bank-info`,
          data: { ...this.bankInfo, preferredCurrency: this.preferredCurrency },
          loadingBar: false,
        });
        this.originalBankInfo = { ...this.bankInfo };
        this.originalPreferredCurrency = this.preferredCurrency;
        alert(res?.data?.message || "정산 설정이 저장되었습니다.");
        this.$emit("save-bank-info", this.bankInfo);
      } catch (error) {
        console.error("정산 설정 저장 실패:", error);
        // 400 은 detail(§7) 또는 error.message(정산_에디터_API.md) 로 오므로 둘 다 그대로 노출합니다.
        const errorMsg = error.response?.data?.detail
            || error.response?.data?.error?.message
            || "정산 설정 저장에 실패했습니다.";
        alert(errorMsg);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.stm-info {
  &__h {
    margin: 0;
    font-size: 14px;
    font-weight: 800;
    color: $p-ink;
  }

  &__loading {
    text-align: center;
    padding: 32px;

    p { margin: 12px 0 0; font-size: 12px; color: $p-ink-soft; }
  }

  &__section-h {
    margin: 0 0 12px;
    font-size: 12px;
    font-weight: 800;
    color: $p-ink2;
    padding: 8px 14px;
    background: $p-panel-alt;
    border-left: 3px solid $p-point-deep;
    border-radius: $p-radius-sm;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    > i { font-size: 16px; color: $p-point-deep; }
  }

  &__divider {
    border-top: 1px solid $p-line;
    margin: 18px 0;
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 6px;

    @media (max-width: 768px) { grid-template-columns: 1fr; }
  }

  &__stat {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    background: $p-panel;
  }

  &__stat-icon {
    width: 44px;
    height: 44px;
    border-radius: $p-radius-md;
    display: grid;
    place-items: center;
    flex-shrink: 0;

    > i { font-size: 20px !important; }

    &--info { background: $p-info-soft; color: $p-info; }
    &--ok   { background: $p-ok-soft;   color: $p-ok; }
    &--warn { background: $p-warn-soft; color: $p-warn; }
  }

  &__stat-label {
    margin: 0;
    font-size: 11px;
    color: $p-ink-soft;
    font-weight: 700;
  }

  &__stat-val {
    margin: 4px 0 0;
    font-family: $p-font-mono;
    font-size: 16px;
    font-weight: 800;
    color: $p-ink;
  }

  &__stat-note {
    display: block;
    margin-top: 3px;
    font-size: 10px;
    font-weight: 600;
    color: $p-ink-soft;
    line-height: 1.35;
  }

  &__load-error {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 12px;
    padding: 10px 14px;
    background: $p-err-soft;
    border-left: 3px solid $p-err;
    border-radius: $p-radius-md;
    font-size: 12px;
    color: $p-err;

    > i { font-size: 16px; }
  }

  &__num {
    font-family: $p-font-mono;
    font-variant-numeric: tabular-nums;
    font-size: 12px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 14px;
  }
}
</style>
